/**
 * Main Application Component
 * 
 * Sets up routing, authentication, and global providers.
 * Uses React.lazy() for route-level code splitting to optimize
 * initial bundle size and startup performance.
 *
 * Route protection is driven by the access-control.ts config.
 * Routes with access: 'authenticated' are wrapped with ProtectedRoute.
 * Routes with access: 'public' render without ProtectedRoute.
 * 
 * Requirements: 1.1, 1.3, 1.7, 15.6, 15.9, 19.7
 */

import { Suspense, lazy, useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme, CircularProgress, Box } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { ProtectedRoute, MainLayout, VisitorLayout } from './components';
import { VoiceRecipeLogo } from './components/VoiceRecipeLogo';
import { ErrorBoundary } from './components/ErrorBoundary';
import { createQueryClient } from './config/query-client';
import { useWebSocket } from './hooks/useWebSocket';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from './lib/stripe';
import { IdleWarningDialog } from './components/IdleWarningDialog';


// Lazy-load route-level components for code splitting
// These are loaded on-demand when the user navigates to the route.
// lazyWithRetry handles stale-chunk errors after deploys by reloading the page.
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
    return lazy(() =>
        factory().catch((err) => {
            // If the chunk failed to load (stale deploy), reload the page once
            // to pick up the new index.html with fresh chunk references.
            const hasReloaded = sessionStorage.getItem('chunk_reload');
            if (!hasReloaded) {
                sessionStorage.setItem('chunk_reload', '1');
                window.location.reload();
                // Return a never-resolving promise so React doesn't render the error
                return new Promise(() => {});
            }
            // Already reloaded once — surface the error to ErrorBoundary
            sessionStorage.removeItem('chunk_reload');
            throw err;
        })
    );
}

const LoginPage = lazyWithRetry(() =>
    import('./pages/LoginPage').then(m => ({ default: m.LoginPage }))
);
const JobManagementPage = lazyWithRetry(() =>
    import('./pages/JobManagementPage').then(m => ({ default: m.JobManagementPage }))
);
const HomepageDashboard = lazyWithRetry(() =>
    import('./pages/HomepageDashboard').then(m => ({ default: m.HomepageDashboard }))
);
const ProjectsPage = lazyWithRetry(() =>
    import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage }))
);
const ProjectDetailPage = lazyWithRetry(() =>
    import('./pages/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage }))
);
const FavoritesPage = lazyWithRetry(() =>
    import('./pages/FavoritesPage').then(m => ({ default: m.FavoritesPage }))
);
const RecipePage = lazyWithRetry(() =>
    import('./pages/RecipePage').then(m => ({ default: m.RecipePage }))
);
const MarketPage = lazyWithRetry(() =>
    import('./pages/MarketPage').then(m => ({ default: m.MarketPage }))
);
const MarketAllRecipesPage = lazyWithRetry(() =>
    import('./pages/MarketAllRecipesPage').then(m => ({ default: m.MarketAllRecipesPage }))
);
const BlogPostPage = lazyWithRetry(() =>
    import('./pages/BlogPostPage').then(m => ({ default: m.BlogPostPage }))
);
const BlogAllPostsPage = lazyWithRetry(() =>
    import('./pages/BlogAllPostsPage').then(m => ({ default: m.BlogAllPostsPage }))
);
const LandingPage = lazyWithRetry(() =>
    import('./pages/LandingPage').then(m => ({ default: m.LandingPage }))
);
const ForumPage = lazyWithRetry(() =>
    import('./pages/ForumPage').then(m => ({ default: m.ForumPage }))
);
const ForumAllThreadsPage = lazyWithRetry(() =>
    import('./pages/ForumAllThreadsPage').then(m => ({ default: m.ForumAllThreadsPage }))
);
const ThreadDetailPage = lazyWithRetry(() =>
    import('./pages/ThreadDetailPage').then(m => ({ default: m.ThreadDetailPage }))
);
const InboxPage = lazyWithRetry(() =>
    import('./pages/InboxPage').then(m => ({ default: m.InboxPage }))
);
const PricingPage = lazyWithRetry(() =>
    import('./pages/PricingPage').then(m => ({ default: m.PricingPage }))
);
const PaymentMethodsPage = lazyWithRetry(() =>
    import('./pages/PaymentMethodsPage').then(m => ({ default: m.PaymentMethodsPage }))
);
const SupportPage = lazyWithRetry(() =>
    import('./pages/SupportPage').then(m => ({ default: m.SupportPage }))
);
const TermsPage = lazyWithRetry(() =>
    import('./pages/TermsPage').then(m => ({ default: m.TermsPage }))
);
const PrivacyPage = lazyWithRetry(() =>
    import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage }))
);
const DmcaPage = lazyWithRetry(() =>
    import('./pages/DmcaPage').then(m => ({ default: m.DmcaPage }))
);
const AccountProfilePage = lazyWithRetry(() =>
    import('./pages/AccountProfilePage').then(m => ({ default: m.AccountProfilePage }))
);
const AccountTransactionsPage = lazyWithRetry(() =>
    import('./pages/AccountTransactionsPage').then(m => ({ default: m.AccountTransactionsPage }))
);
const PlayerPage = lazyWithRetry(() =>
    import('./pages/PlayerPage').then(m => ({ default: m.PlayerPage }))
);
const CreateJobPage = lazyWithRetry(() =>
    import('./pages/CreateJobPage').then(m => ({ default: m.CreateJobPage }))
);
const JobDetailPage = lazyWithRetry(() =>
    import('./pages/JobDetailPage').then(m => ({ default: m.JobDetailPage }))
);
const CreateRecipePage = lazyWithRetry(() =>
    import('./pages/CreateRecipePage').then(m => ({ default: m.CreateRecipePage }))
);
const RecipeDetailPage = lazyWithRetry(() =>
    import('./pages/RecipeDetailPage').then(m => ({ default: m.RecipeDetailPage }))
);
const MarketRecipeDetailPage = lazyWithRetry(() =>
    import('./pages/MarketRecipeDetailPage').then(m => ({ default: m.MarketRecipeDetailPage }))
);
const RecipeEditPage = lazyWithRetry(() =>
    import('./pages/RecipeEditPage').then(m => ({ default: m.RecipeEditPage }))
);
const CreateProjectPage = lazyWithRetry(() =>
    import('./pages/CreateProjectPage').then(m => ({ default: m.CreateProjectPage }))
);
const EditProjectPage = lazyWithRetry(() =>
    import('./pages/EditProjectPage').then(m => ({ default: m.EditProjectPage }))
);
const AssignProjectPage = lazyWithRetry(() =>
    import('./pages/AssignProjectPage').then(m => ({ default: m.AssignProjectPage }))
);
const CreateThreadPage = lazyWithRetry(() =>
    import('./pages/CreateThreadPage').then(m => ({ default: m.CreateThreadPage }))
);
const ManageRolesPage = lazyWithRetry(() =>
    import('./pages/ManageRolesPage').then(m => ({ default: m.ManageRolesPage }))
);
const CreateRolePage = lazyWithRetry(() =>
    import('./pages/CreateRolePage').then(m => ({ default: m.CreateRolePage }))
);


// Shared palette tokens
const lightPalette = {
    mode: 'light' as const,
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
};

const darkPalette = {
    mode: 'dark' as const,
    primary: { main: '#90caf9' },
    secondary: { main: '#f48fb1' },
    background: { default: '#121212', paper: '#1e1e1e' },
    text: { primary: '#e0e0e0', secondary: '#a0a0a0' },
};

export function buildTheme(effectiveTheme: 'light' | 'dark') {
    const darkComponents = {
        MuiAppBar: { styleOverrides: { root: { backgroundColor: '#1e1e1e' } } },
        MuiDrawer: { styleOverrides: { paper: { backgroundColor: '#1e1e1e' } } },
        MuiCard: { styleOverrides: { root: { backgroundColor: '#1e1e1e' } } },
        MuiDialog: { styleOverrides: { paper: { backgroundColor: '#1e1e1e' } } },
        MuiAlert: { styleOverrides: { root: { backgroundColor: '#2c2c2c' } } },
        MuiTableCell: { styleOverrides: { root: { borderBottomColor: '#333' } } },
    };

    return createTheme({
        palette: effectiveTheme === 'dark' ? darkPalette : lightPalette,
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h1: { fontWeight: 600, letterSpacing: '-0.02em' },
            h2: { fontWeight: 600, letterSpacing: '-0.02em' },
            h3: { fontWeight: 600, letterSpacing: '-0.02em' },
            h4: { fontWeight: 600, letterSpacing: '-0.02em' },
            h5: { fontWeight: 600, letterSpacing: '-0.02em' },
            h6: { fontWeight: 600, letterSpacing: '-0.02em' },
            subtitle1: { fontWeight: 500 },
            subtitle2: { fontWeight: 500 },
            body1: { fontWeight: 400, letterSpacing: '0.01em' },
            body2: { fontWeight: 400, letterSpacing: '0.01em' },
            button: { fontWeight: 700 },
        },
        shape: {
            borderRadius: 10,
        },
        components: {
            ...(effectiveTheme === 'dark' ? darkComponents : {}),
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                        },
                    },
                },
            },
        },
    });
}

// Create React Query client with configured defaults
const queryClient = createQueryClient();

/**
 * Loading fallback displayed while lazy-loaded components are being fetched.
 */
function PageLoadingFallback() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                gap: 3,
            }}
        >
            <VoiceRecipeLogo size="large" />
            <CircularProgress size={40} />
        </Box>
    );
}

/**
 * Layout route component for authenticated routes.
 * Renders MainLayout wrapped in ProtectedRoute, used as the element
 * of a pathless parent Route that nests all auth-required child routes.
 */
function AuthenticatedLayoutRoute() {
    return (
        <ProtectedRoute>
            <MainLayout />
        </ProtectedRoute>
    );
}

/**
 * Layout route component for visitor-only routes.
 * Renders VisitorLayout as a pathless parent Route element
 * that nests public child routes (e.g., /terms, /privacy).
 */
function VisitorLayoutRoute() {
    return <VisitorLayout />;
}

/**
 * Dual-layout wrapper that conditionally renders children inside
 * MainLayout for authenticated users or VisitorLayout for visitors.
 * Shows a loading fallback while auth state is being determined.
 */
function DualLayoutRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuth();
    if (isLoading) return <PageLoadingFallback />;
    if (isAuthenticated) {
        return <MainLayout>{children}</MainLayout>;
    }
    return <VisitorLayout>{children}</VisitorLayout>;
}

/**
 * Landing-route wrapper. Visitors see the marketing LandingPage as
 * usual; authenticated users skip the marketing page and land on the
 * page they were on before sign-in (when available) or the dashboard.
 *
 * Implementation: handleGoogleSignIn writes `post_signin_redirect` to
 * localStorage just before kicking off the OAuth flow. Cognito returns
 * the user to '/' (the configured redirectSignIn URL); we read that
 * value here and route them home.
 */
/**
 * Captures the post-signin redirect target exactly once per page load.
 *
 * Why a module-level cache instead of useState/useEffect:
 * React 18's StrictMode (dev only) intentionally mounts → unmounts →
 * remounts every component to surface side-effect bugs. A useState
 * lazy initializer or a useEffect would still run twice across the two
 * mounts: the first run reads `/forum` from localStorage and removes
 * it, the second run finds nothing and falls back to `/dashboard` —
 * the second `<Navigate>` wins and the user lands on the wrong page.
 *
 * Reading + clearing localStorage at module scope happens before any
 * React rendering and is naturally idempotent (the cached value is
 * reused across both mounts). After a real page navigation (full
 * reload after OAuth), the module is fresh and a new value is
 * captured.
 */
let cachedPostSigninRedirect: string | null | undefined = undefined;
/** Whether this page load is an OAuth bounce (social_login_provider was set). */

/**
 * Returns a same-origin path to redirect to after sign-in, or null when
 * there's no pending redirect. The cached value is read once per
 * LandingRoute lifecycle (survives StrictMode double-mount) and cleared
 * from localStorage on first read. The cache is reset on LandingRoute
 * unmount so a subsequent visit to '/' (e.g. clicking the logo) reads
 * fresh state.
 */
function consumePostSigninRedirect(): string | null {
    if (cachedPostSigninRedirect !== undefined) {
        return cachedPostSigninRedirect;
    }
    let target: string | null = null;
    try {
        const saved = localStorage.getItem('post_signin_redirect');
        if (saved && saved.startsWith('/') && !saved.startsWith('//')) {
            target = saved;
        }
        localStorage.removeItem('post_signin_redirect');
    } catch {
        // localStorage may be unavailable in private mode — fall through.
    }
    cachedPostSigninRedirect = target;
    return target;
}

/** Reset on LandingRoute unmount so the next visit reads localStorage fresh. */
function resetPostSigninRedirectCache() {
    cachedPostSigninRedirect = undefined;
}

/**
 * Detect whether this page load is an OAuth bounce. Reads and removes
 * social_login_provider from localStorage on first call. The result is
 * cached for the lifetime of this module (single page load) so
 * subsequent renders of LandingRoute don't re-read a stale value.
 *
 * MainLayout also reads social_login_provider for the account-linked
 * notification — but it runs AFTER LandingRoute redirects away from '/',
 * so by the time MainLayout mounts, the key is already consumed here.
 * MainLayout's useEffect will find it gone and skip the notification.
 * That's acceptable: the redirect to /dashboard IS the "you're signed in"
 * signal; the toast is redundant on OAuth bounce.
 */
let _oauthBounceConsumed = false;
let _wasOAuthBounce = false;

function wasOAuthBounce(): boolean {
    if (_oauthBounceConsumed) return false; // Only redirect once per session
    _oauthBounceConsumed = true;
    try {
        const val = localStorage.getItem('social_login_provider');
        _wasOAuthBounce = !!val;
        if (val) localStorage.removeItem('social_login_provider');
    } catch {
        _wasOAuthBounce = false;
    }
    return _wasOAuthBounce;
}

/** Reset on LandingRoute unmount so a fresh visit re-reads localStorage. */
function resetOAuthBounceCache() {
    _oauthBounceConsumed = false;
    _wasOAuthBounce = false;
}

function LandingRoute() {
    const { isAuthenticated, isLoading } = useAuth();

    // Clear caches on unmount so the next visit to '/' (e.g. clicking the
    // logo) reads fresh state instead of replaying the previous bounce.
    useEffect(() => {
        return () => {
            resetPostSigninRedirectCache();
            resetOAuthBounceCache();
        };
    }, []);

    if (isLoading) return <PageLoadingFallback />;

    if (isAuthenticated) {
        // Case 1: User just completed sign-in (OAuth bounce or email sign-in
        // that redirected here). Redirect to their intended destination or
        // /dashboard as the default.
        const pending = consumePostSigninRedirect();
        if (pending) {
            return <Navigate to={pending} replace />;
        }
        if (wasOAuthBounce()) {
            return <Navigate to="/dashboard" replace />;
        }
        // Case 2: Already signed-in user navigating to Home — show the
        // landing page. No redirect.
    }
    return <DualLayoutRoute><LandingPage /></DualLayoutRoute>;
}

function WebSocketPresence() {
    useWebSocket();
    return null;
}

function PreviewLoginRedirect() {
    const { notify } = useNotification();
    useEffect(() => {
        notify({ message: 'Sign-in is disabled in preview mode', severity: 'info', autoCloseDuration: 3000 });
    }, [notify]);
    return <Navigate to="/" replace />;
}

/**
 * Inner component that reads the effective theme from context and
 * provides the corresponding MUI ThemeProvider.
 */
function ThemedApp() {
    const { effectiveTheme } = useThemeMode();
    const theme = useMemo(() => buildTheme(effectiveTheme), [effectiveTheme]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {import.meta.env.VITE_PREVIEW_MODE === 'true' && (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 9999,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        textAlign: 'center',
                        py: 0.5,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        letterSpacing: 0.5,
                    }}
                >
                    This is a Preview
                </Box>
            )}
            <NotificationProvider>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                        <WebSocketPresence />
                        <IdleWarningDialog />
                        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
                            <Suspense fallback={<PageLoadingFallback />}>
                                <Routes>
                                    {/* Public route - Login (lazy-loaded; preview mode redirects home) */}
                                    <Route path="/login" element={
                                        import.meta.env.VITE_PREVIEW_MODE === 'true'
                                            ? <PreviewLoginRedirect />
                                            : <LoginPage />
                                    } />

                                    {/* Landing Page — visitors see the marketing
                                        page; signed-in users get redirected to /dashboard. */}
                                    <Route
                                        path="/"
                                        element={<LandingRoute />}
                                    />

                                    {/* Authenticated layout route (persistent MainLayout) */}
                                    <Route element={<AuthenticatedLayoutRoute />}>
                                        <Route path="/dashboard" element={<HomepageDashboard />} />
                                        <Route path="/jobs" element={<JobManagementPage />} />
                                        <Route path="/jobs/:jobId" element={<JobDetailPage />} />
                                        <Route path="/jobs/:jobId/assign-project" element={<AssignProjectPage />} />
                                        <Route path="/projects" element={<ProjectsPage />} />
                                        <Route path="/projects/create" element={<CreateProjectPage />} />
                                        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                                        <Route path="/projects/:projectId/edit" element={<EditProjectPage />} />
                                        <Route path="/favorites" element={<FavoritesPage />} />
                                        <Route path="/recipes" element={<RecipePage />} />
                                        <Route path="/recipes/create" element={<CreateRecipePage />} />
                                        <Route path="/recipes/:recipeId" element={<RecipeDetailPage />} />
                                        <Route path="/recipes/:recipeId/edit" element={<RecipeEditPage />} />
                                        <Route path="/recipes/market/:marketRecipeId" element={<MarketRecipeDetailPage />} />
                                        <Route path="/create" element={<CreateJobPage />} />
                                        <Route path="/create/:mode" element={<CreateJobPage />} />
                                        <Route path="/payment-methods" element={<Elements stripe={stripePromise}><PaymentMethodsPage /></Elements>} />
                                        <Route path="/roles" element={<ManageRolesPage />} />
                                        <Route path="/roles/create" element={<CreateRolePage />} />
                                        <Route path="/roles/edit/:roleId" element={<CreateRolePage />} />
                                        <Route path="/inbox" element={<InboxPage />} />
                                        <Route path="/account/profile" element={<AccountProfilePage />} />
                                        <Route path="/account/transactions" element={<AccountTransactionsPage />} />
                                        <Route path="/forum/create" element={<CreateThreadPage />} />
                                    </Route>

                                    {/* Public route - Market All Recipes (visitors see public marketing recipes) */}
                                    <Route
                                        path="/market/all"
                                        element={<DualLayoutRoute><MarketAllRecipesPage /></DualLayoutRoute>}
                                    />

                                    {/* Public route - Market (visitors see public marketing recipes) */}
                                    <Route
                                        path="/market"
                                        element={<DualLayoutRoute><MarketPage /></DualLayoutRoute>}
                                    />

                                    {/* Public route - Pricing (access-control.ts: public) */}
                                    <Route
                                        path="/pricing"
                                        element={<DualLayoutRoute>{stripePromise ? <Elements stripe={stripePromise}><PricingPage /></Elements> : <PricingPage />}</DualLayoutRoute>}
                                    />

                                    {/* Public route - Support (access-control.ts: public) */}
                                    <Route
                                        path="/support"
                                        element={<DualLayoutRoute><SupportPage /></DualLayoutRoute>}
                                    />

                                    {/* Public route - Blog All Posts */}
                                    <Route
                                        path="/blog"
                                        element={
                                            <DualLayoutRoute>
                                                <BlogAllPostsPage />
                                            </DualLayoutRoute>
                                        }
                                    />

                                    {/* Public route - Blog Post Detail (access-control.ts: public, with gated actions) */}
                                    <Route
                                        path="/blog/:postId"
                                        element={
                                            <DualLayoutRoute>
                                                <BlogPostPage />
                                            </DualLayoutRoute>
                                        }
                                    />

                                    {/* Public route - Forum (visitors see public marketing threads) */}
                                    <Route
                                        path="/forum"
                                        element={
                                            <DualLayoutRoute>
                                                <ForumPage />
                                            </DualLayoutRoute>
                                        }
                                    />

                                    {/* Public route - Forum All Threads (visitors see public marketing threads) */}
                                    <Route
                                        path="/forum/all"
                                        element={
                                            <DualLayoutRoute>
                                                <ForumAllThreadsPage />
                                            </DualLayoutRoute>
                                        }
                                    />

                                    {/* Thread Detail - public marketing threads accessible without auth, others require sign-in */}
                                    <Route
                                        path="/forum/:threadId"
                                        element={
                                            <DualLayoutRoute>
                                                <ThreadDetailPage />
                                            </DualLayoutRoute>
                                        }
                                    />

                                    {/* Visitor-only layout routes */}
                                    <Route element={<VisitorLayoutRoute />}>
                                        <Route path="/terms" element={<TermsPage />} />
                                        <Route path="/privacy" element={<PrivacyPage />} />
                                        <Route path="/dmca" element={<DmcaPage />} />
                                    </Route>

                                    {/* Standalone player popup (no layout wrapper) */}
                                    <Route path="/player" element={<PlayerPage />} />

                                    {/* Redirect any unknown routes to home */}
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                    </AuthProvider>
                </QueryClientProvider>
            </NotificationProvider>
        </ThemeProvider>
    );
}

/**
 * App Component
 * 
 * Root component that provides:
 * - Error boundary for error handling
 * - Theme mode context (light/dark/system)
 * - Material-UI theming
 * - React Query for data fetching
 * - AWS Amplify authentication
 * - React Router for navigation
 * - Protected routes for authenticated pages
 * - Suspense boundaries for lazy-loaded route components
 */
function App() {
    return (
        <ErrorBoundary>
            <ThemeModeProvider>
                <ThemedApp />
            </ThemeModeProvider>
        </ErrorBoundary>
    );
}

export default App;
